import { mergeAttributes, Node } from "@tiptap/core";
import type { MarkdownToken } from "@tiptap/core";

/**
 * Tables with `\|` escaped pipes or HTML inside cells break in
 * @tiptap/markdown — escaped pipes lose their backslash on save (which splits
 * the cell on the next load) and HTML is dropped at parse. So we check for
 * those and render them as raw markdown; simple tables render just fine as a
 * first-class editable table.
 *
 * It's a plain syntactic check because the fancier alternative — serialize
 * each table and compare — has to run inside the marked tokenizer, where
 * creating a nested parser corrupts the outer document. The fixture
 * round-trip tests will flag any lossy construct we haven't listed here.
 */
function isPromotableTable(source: string): boolean {
  if (/\\\|/.test(source)) return false;
  // Comment markers are the one HTML the editor reads back faithfully.
  if (/<(?!span data-ht-comment=)[a-zA-Z]/.test(source)) return false;
  return true;
}

/** ProseMirror node name for verbatim-preserved out-of-schema markdown. */
const RAW_MARKDOWN_BLOCK_NAME = "rawMarkdownBlock";

/** Marked token name emitted by the raw-markdown-block tokenizer. */
const RAW_MARKDOWN_BLOCK_TOKEN = "rawMarkdownBlock";

/**
 * Match a GFM table at the start of `src`: a header row containing a pipe, a
 * delimiter row (dashes/colons separated by pipes), then contiguous body rows.
 */
function matchTable(src: string): { raw: string; source: string } | undefined {
  const lines = src.split("\n");
  if (lines.length < 2) return undefined;

  const [header, delimiter] = lines;
  if (header === undefined || delimiter === undefined) return undefined;
  if (!header.includes("|")) return undefined;

  const delimiterPattern =
    /^ {0,3}\|?[ \t]*:?-{1,}:?[ \t]*(?:\|[ \t]*:?-{1,}:?[ \t]*)*\|?[ \t]*$/;
  if (!delimiterPattern.test(delimiter)) return undefined;

  const collected = [header, delimiter];
  for (let i = 2; i < lines.length; i++) {
    const line = lines[i];
    if (!line || line.trim() === "" || !line.includes("|")) break;
    collected.push(line);
  }

  const source = collected.join("\n");
  return { raw: source, source };
}

/**
 * Catches markdown the editor can't represent faithfully and keeps it
 * verbatim instead of letting it get dropped or mangled. Its tokenizer sees
 * every GFM table before marked's own table tokenizer: simple tables pass
 * through (return undefined) and become normal editable tables, while lossy
 * ones (see isPromotableTable) are stored as their original source, shown
 * read-only, and written back byte-identical on save.
 */
export const RawMarkdownBlock = Node.create({
  name: RAW_MARKDOWN_BLOCK_NAME,
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,
  isolating: true,

  addAttributes() {
    return {
      source: {
        default: "",
        parseHTML: (element) => element.textContent ?? "",
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "pre[data-raw-markdown]",
        getAttrs: (element) => ({
          source: (element as HTMLElement).textContent ?? "",
        }),
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "pre",
      mergeAttributes(HTMLAttributes, {
        "data-raw-markdown": "",
        class: "ht-md-raw-block",
      }),
      node.attrs.source ?? "",
    ];
  },

  markdownTokenName: RAW_MARKDOWN_BLOCK_TOKEN,

  markdownTokenizer: {
    name: RAW_MARKDOWN_BLOCK_TOKEN,
    level: "block",
    start(src: string) {
      // Only flag spots that actually look like a table: a pipe line followed
      // by a delimiter row. If we flag a spot and then decline it in
      // tokenize(), marked chews through the paragraph one character at a
      // time and it renders one letter per line — so a stray `|` in a
      // sentence must never match here. -1 means "no candidate in this slice".
      return src.search(/(?:^|\n)[^\n]*\|[^\n]*\n {0,3}\|?[ \t]*:?-/);
    },
    tokenize(src: string) {
      const matched = matchTable(src);
      if (!matched) return undefined;
      // Simple tables fall through to marked's table tokenizer and become
      // real editable table nodes; only lossy ones are claimed verbatim.
      if (isPromotableTable(matched.source)) return undefined;
      return {
        type: RAW_MARKDOWN_BLOCK_TOKEN,
        raw: matched.raw,
        source: matched.source,
      };
    },
  },

  parseMarkdown(token: MarkdownToken) {
    const source =
      typeof token.source === "string"
        ? token.source
        : (token.raw ?? "").replace(/\r?\n$/, "");
    return {
      type: RAW_MARKDOWN_BLOCK_NAME,
      attrs: { source },
    };
  },

  renderMarkdown(node) {
    return node.attrs?.source ?? "";
  },
});
